import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, ArrowLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`/api/blog.json`);
      const allPosts = response.data;
      const found = allPosts.find((p) => p.id === id);
      if (found) {
        setPost(found);
        setRelatedPosts(allPosts.filter((p) => p.id !== id).slice(0, 2));
      } else {
        setPost(null);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#0F1115] mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#D4AF37] font-semibold hover:underline">Return to Blog</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <Helmet>
        <title>{post.title} | Oracle Project Group Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://oracleprojectgroup.com/blog/${post.id}`} />
      </Helmet>

      {/* Hero */}
      <header className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-300 mb-6">
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-[#C6A45B]" />
              <span>{new Date(post.date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={16} className="text-[#C6A45B]" />
              <span>{post.readTime} min read</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">{post.title}</h1>
          {post.author && (
            <p className="mt-4 text-gray-300">By {post.author}</p>
          )}
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="bg-[#F6F6F7] py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center space-x-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/blog" className="hover:text-[#D4AF37] transition-colors">Blog</Link>
          <ChevronRight size={14} />
          <span className="text-[#0F1115] font-medium truncate max-w-xs">{post.title}</span>
        </div>
      </nav>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 mb-8 leading-relaxed font-medium border-l-4 border-[#D4AF37] pl-6">
            {post.excerpt}
          </p>
          {post.content && (
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              {post.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="mb-6 text-gray-700 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-[#F6F6F7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0F1115] mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedPosts.map((related) => (
                <div key={related.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                  <img
                    src={related.image}
                    alt={related.title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} className="text-[#C6A45B]" />
                        <span>{new Date(related.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} className="text-[#C6A45B]" />
                        <span>{related.readTime} min read</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#0F1115] mb-3">{related.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{related.excerpt}</p>
                    <Link
                      to={`/blog/${related.id}`}
                      className="inline-flex items-center text-[#D4AF37] font-semibold hover:text-[#C19A2B] transition-colors"
                    >
                      Read More
                      <ArrowLeft className="ml-2 rotate-180" size={16} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <div className="py-8 bg-white text-center">
        <Link
          to="/blog"
          className="inline-flex items-center text-[#D4AF37] font-semibold hover:text-[#C19A2B] transition-colors text-lg"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Blog
        </Link>
      </div>
    </main>
  );
};

export default BlogPost;
